/**
 * UserGuide — Role-aware in-app help centre
 * Covers: My Role & Capabilities, Key Workflows, Navigation Map, Glossary
 */
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen, ChevronDown, ChevronRight, User, Target, Activity,
  MessageSquare, TrendingUp, Building2, Users, Calendar,
  ClipboardList, Settings2, FileText, Search, CheckCircle2,
  XCircle, AlertTriangle, Lightbulb, MapPin, ArrowRight,
  HelpCircle, Tag, Filter, Phone, Mail, Zap, Shield,
} from "lucide-react";

// ── Data ──────────────────────────────────────────────────────────────────────

const ROLE_LABELS = {
  BUSINESS_OWNER:          "Business Owner",
  CRM_MANAGER:             "CRM Manager",
  RELATIONSHIP_MANAGER:    "Relationship Manager",
  PROJECT_MANAGER:         "Project Manager",
  EVENT_PLANNER_DESIGNER:  "Event Planner / Designer",
  TENANT_ADMIN:            "Tenant Administrator",
  PLATFORM_ADMIN:          "Platform Admin",
  EVENT_MANAGER:           "Event Manager",
  // Retired — backward-compat display for legacy sessions only
  SALES_CRM_USER:          "Sales / CRM Executive",
  EVENT_POC:               "Event POC",
};

const ROLE_DESCRIPTIONS = {
  BUSINESS_OWNER:         "Executive oversight of the CRM pipeline. Assigns Relationship Managers, reviews business performance, and ensures the team moves leads forward.",
  CRM_MANAGER:            "Full lead lifecycle management — from capture to conversion. Manages team performance, handles duplicate resolution, and drives qualification.",
  RELATIONSHIP_MANAGER:   "Commercial lead owner. Claims leads from the pool, qualifies prospects, drives the sales cycle, and owns the customer relationship through to conversion.",
  PROJECT_MANAGER:        "Event execution owner. Manages planning milestones, vendor coordination, and delivery for active events in their portfolio.",
  EVENT_PLANNER_DESIGNER: "Assigned-case specialist. Builds planning briefs, updates event intent, logs activities, and reviews AI recommendations — for assigned leads only.",
  TENANT_ADMIN:           "Platform configuration authority. Manages users, CRM settings, email channels, and reviews audit trails.",
};

const ROLE_CAN = {
  BUSINESS_OWNER: [
    "View full CRM pipeline and performance reports",
    "Assign a Relationship Manager to any lead",
    "Read all leads, accounts, contacts, opportunities",
    "Review business dashboard and KPI summary",
    "Read communication history for any customer",
  ],
  CRM_MANAGER: [
    "Create, update, and transition any lead",
    "Qualify leads and manage qualification scores",
    "Assign and reassign Relationship Managers on leads",
    "Manage accounts, contacts, opportunities in full",
    "Log activities: calls, meetings, notes, tasks",
    "Schedule and manage follow-ups",
    "Handle duplicate contact/account resolution",
    "View CRM analytics and team performance",
    "Access Communication Inbox",
  ],
  RELATIONSHIP_MANAGER: [
    "Claim unassigned leads from the pool",
    "Own and manage your leads through the full sales cycle",
    "Qualify leads and update event intent",
    "Log calls, meetings, notes, tasks on your leads",
    "Schedule and manage follow-ups",
    "Send and receive messages via Communication Inbox",
    "View customer journey timeline",
    "Convert qualified leads to events",
  ],
  PROJECT_MANAGER: [
    "View leads and events in your project portfolio",
    "Manage event planning milestones and readiness gates",
    "Log activities and follow-ups on active events",
    "View and update event execution details",
    "Access Communication Inbox for assigned events",
  ],
  EVENT_PLANNER_DESIGNER: [
    "View leads assigned to you",
    "Update event planning fields: date, guests, location, budget, scope",
    "Update Event Intent: services of interest, requirements",
    "Log activities (tasks, meetings, notes) on assigned leads",
    "Schedule and complete follow-ups",
    "View AI recommendations for assigned cases",
    "Access Communication Inbox for assigned customers",
  ],
  TENANT_ADMIN: [
    "Create and manage user accounts",
    "Assign and change user roles",
    "Configure CRM setup domains",
    "Configure email channels",
    "Review the full audit trail",
    "Manage administration settings",
  ],
};

const ROLE_CANNOT = {
  BUSINESS_OWNER: [
    "Create new leads directly",
    "Log activities or follow-ups",
    "Manage communication channels",
    "Access admin/setup configuration",
  ],
  CRM_MANAGER: [
    "Access finance or billing modules",
    "Modify platform-level setup",
    "Create or delete user accounts",
  ],
  RELATIONSHIP_MANAGER: [
    "Globally reassign another RM's lead (requires CRM Manager or Business Owner)",
    "Access finance or admin modules",
    "Create or delete user accounts",
    "Assign tasks or leads to other Relationship Managers",
  ],
  PROJECT_MANAGER: [
    "Create or claim new leads directly",
    "Qualify or convert leads",
    "Access finance or admin modules",
    "Assign leads to Relationship Managers",
  ],
  EVENT_PLANNER_DESIGNER: [
    "View leads not assigned to you",
    "Qualify or convert leads",
    "Assign or change the Relationship Manager on a lead",
    "Create accounts or contacts",
    "Access finance, admin, or platform setup",
    "Transition lead status (e.g. NEW → QUALIFIED)",
  ],
  TENANT_ADMIN: [
    "Manage day-to-day leads, activities, follow-ups",
    "Access finance modules (separate admin)",
  ],
};

// ── Workflows per role ─────────────────────────────────────────────────────────

const WORKFLOWS = {
  BUSINESS_OWNER: [
    {
      title: "Review the sales pipeline",
      steps: [
        { nav: "Home", action: "The dashboard shows the full pipeline funnel — leads by status, qualified count, and conversion rate." },
        { nav: "Home → CRM Reports tab", action: "View team performance breakdown by agent, and pipeline KPIs." },
      ],
    },
    {
      title: "Assign a Relationship Manager to a lead",
      steps: [
        { nav: "Leads", action: "Find the lead using search or filters. Click to open it." },
        { nav: "Lead Detail → Ownership card", action: "Click the 'Assign Relationship Manager' button (visible to Business Owner and CRM Manager)." },
        { nav: "Assign RM modal", action: "Select a Relationship Manager from the dropdown. Optionally add a reason. Confirm." },
      ],
    },
    {
      title: "Review leads flagged for Planning Review",
      steps: [
        { nav: "Leads", action: "Use the Qualification filter → select 'Planning Review Required'." },
        { nav: "Lead Detail", action: "Open the lead. Review event details and scoring in the Overview tab." },
        { nav: "Lead Detail → Status path bar", action: "Transition to 'Planning Review' status, then after review move to 'Qualified' or 'Disqualified'." },
      ],
    },
  ],
  CRM_MANAGER: [
    {
      title: "Qualify a lead",
      steps: [
        { nav: "Leads", action: "Open the lead you want to qualify." },
        { nav: "Lead Detail → Overview tab", action: "Check the qualification score card — it shows what's known and what's missing." },
        { nav: "Lead Detail → Activity tab", action: "Click the 'Qualify' button (AI-assisted scoring runs and updates the qualification status)." },
        { nav: "Status path bar", action: "If score ≥ 75 → lead moves to QUALIFIED. If missing info → PARTIALLY_QUALIFIED. If guest ≥ 500 → PLANNING_REVIEW_REQUIRED." },
      ],
    },
    {
      title: "Handle duplicate contact or account",
      steps: [
        { nav: "Lead Detail → Related tab", action: "A 'Duplicate candidates' section appears when a similar contact/account already exists." },
        { nav: "Related tab → Duplicate card", action: "Review the candidate. Choose to 'Use Existing' (merge) or 'Create New' to keep them separate." },
        { nav: "Start Planning flow", action: "Duplicate resolution is also triggered automatically when you Start Event Planning on a qualified lead." },
      ],
    },
    {
      title: "Convert a qualified lead",
      steps: [
        { nav: "Lead Detail", action: "Lead must be in QUALIFIED status. The Convert button appears in the header action area." },
        { nav: "Conversion flow", action: "The system creates (or reuses) Account + Contact + Opportunity automatically." },
        { nav: "Lead Detail", action: "Lead status changes to CONVERTED. Linked records appear in the Related tab." },
      ],
    },
    {
      title: "Start Event Planning",
      steps: [
        { nav: "Opportunity", action: "Opportunity must be at CLOSED_WON stage." },
        { nav: "Lead Detail → header", action: "Click 'Start Event Planning'. If there are duplicates, the resolution modal appears first." },
        { nav: "Confirm", action: "Exactly one Event Draft and Brief are created (idempotent — clicking twice does not create two events)." },
      ],
    },
  ],
  RELATIONSHIP_MANAGER: [
    {
      title: "Claim an unassigned lead",
      steps: [
        { nav: "Leads", action: "Look for leads with no Relationship Manager shown in the Ownership card." },
        { nav: "Lead Detail → Ownership card", action: "Click 'Claim This Lead'. The lead is immediately assigned to you." },
        { nav: "Your lead list", action: "The lead now appears with you as owner. It will not be claimable by other RMs." },
      ],
    },
    {
      title: "Qualify a lead",
      steps: [
        { nav: "Lead Detail → Overview tab", action: "Review the qualification score card. Missing fields are listed with their point value." },
        { nav: "Lead Detail → Event Intent tab", action: "Fill in missing event details: date, budget, location, guest count, venue status." },
        { nav: "Activity tab → Qualify", action: "Click Qualify to run AI-assisted scoring. Score ≥ 75 → QUALIFIED." },
      ],
    },
    {
      title: "Log a customer call",
      steps: [
        { nav: "Lead Detail → Activity tab", action: "Click 'Log Call'." },
        { nav: "Log Call modal", action: "Enter: outcome (Reached / No Answer / Left Voicemail), duration, summary notes." },
        { nav: "Save", action: "The call appears in the Recent Activity timeline on the lead." },
      ],
    },
    {
      title: "Send a message to the customer",
      steps: [
        { nav: "Lead Detail → Activity tab", action: "Click 'Send Message'." },
        { nav: "Send Message modal", action: "Type your message. Choose the channel (WhatsApp if the customer came via WhatsApp, Email otherwise)." },
        { nav: "Send", action: "Message is logged in the Activity timeline and sent to the customer (Email/WhatsApp)." },
      ],
    },
  ],
  PROJECT_MANAGER: [
    {
      title: "Review your event portfolio",
      steps: [
        { nav: "Active Events (sidebar)", action: "All events assigned to you or your team are listed here." },
        { nav: "Event card", action: "See status, readiness gates, open issues, and upcoming milestones at a glance." },
        { nav: "Event 360 view", action: "Full event overview: brief, blueprint, threads, and change history." },
      ],
    },
    {
      title: "Review readiness milestones",
      steps: [
        { nav: "Active Events → select an event", action: "Readiness milestones (Design, Commercial, Partner, Operations, Event Day) appear in the Milestones section." },
        { nav: "Milestone gates", action: "Each gate shows its status: READY, ATTENTION, BLOCKED, or NOT_READY." },
        { nav: "Issues", action: "Open issues attached to a gate explain what is blocking it. Resolve each issue to unblock the gate." },
      ],
    },
    {
      title: "Log an activity on an event",
      steps: [
        { nav: "Event → threads or operations panel", action: "Select the relevant thread or card." },
        { nav: "Activity or note", action: "Log a task, note, or call update against the thread to capture progress." },
      ],
    },
  ],
  EVENT_PLANNER_DESIGNER: [
    {
      title: "View your assigned cases",
      steps: [
        { nav: "My Planning Cases (home screen)", action: "All leads assigned to you appear here with their planning readiness %, missing info, and next action." },
        { nav: "Case card → Event Intent / Recommendations / Brief buttons", action: "Quick links to jump directly to that section within the lead." },
        { nav: "Leads (Assigned) in sidebar", action: "Alternative list view with filter/search options." },
      ],
    },
    {
      title: "Update event planning details",
      steps: [
        { nav: "My Planning Cases → click a case", action: "Opens the Lead Detail for that case." },
        { nav: "Lead Detail → Event Intent tab", action: "Edit: event type, date, location, guest count, budget range, venue status, services of interest, customer requirements." },
        { nav: "Save", action: "Changes are saved. The qualification score recalculates automatically." },
      ],
    },
    {
      title: "Log an activity on a case",
      steps: [
        { nav: "Lead Detail → Activity tab", action: "Use the quick-action buttons: Add Task, Log Call, Add Meeting, Send Message, Follow-up, Add Note." },
        { nav: "Fill in the modal", action: "Enter details. All fields relevant to your role are available." },
        { nav: "Save", action: "Activity appears in the Recent section of the timeline." },
      ],
    },
    {
      title: "Review AI recommendations",
      steps: [
        { nav: "Lead Detail → AI Insights tab", action: "Recommendations generated by the platform's policy engine appear here." },
        { nav: "Each recommendation card", action: "Shows the suggested action, confidence level, and reason." },
        { nav: "Accept / Reject / Modify", action: "You can accept (applies the suggestion), reject (with a reason), or modify it before applying." },
      ],
    },
  ],
  TENANT_ADMIN: [
    {
      title: "Create a new user",
      steps: [
        { nav: "Users & Roles (sidebar → Administration)", action: "Click '+ New User' button." },
        { nav: "User form", action: "Enter: name, email, role. The user receives login credentials." },
        { nav: "Save", action: "User is active immediately." },
      ],
    },
    {
      title: "Configure CRM setup domains",
      steps: [
        { nav: "Setup Studio (sidebar → Administration)", action: "Each domain card shows its activation status." },
        { nav: "Click a domain", action: "Review readiness requirements. Validate → Submit for Approval → Approve → Activate." },
        { nav: "Domain active", action: "The domain's features are live for the tenant." },
      ],
    },
    {
      title: "Review the audit trail",
      steps: [
        { nav: "Audit (sidebar → Administration)", action: "Full log of all actions: logins, lead changes, user creation, configuration changes." },
        { nav: "Filters", action: "Filter by action type, actor, entity, or date range." },
      ],
    },
  ],
};

// ── Navigation Map ─────────────────────────────────────────────────────────────

const NAV_MAP = [
  { section: "Home / Reports", path: "/home", roles: ["BUSINESS_OWNER", "CRM_MANAGER", "RELATIONSHIP_MANAGER", "PROJECT_MANAGER", "TENANT_ADMIN"],
    desc: "Business dashboard: pipeline funnel, KPIs, agent breakdown, recent activity." },
  { section: "Leads", path: "/engage/leads", roles: ["CRM_MANAGER", "RELATIONSHIP_MANAGER", "PROJECT_MANAGER", "EVENT_PLANNER_DESIGNER"],
    desc: "Full lead list with filters (status, qualification, owner, date range). Click any row to open Lead Detail." },
  { section: "Lead Detail", path: "/engage/leads/:id", roles: ["ALL"],
    desc: "Tabs: Overview (scoring + info), Activity (tasks, calls, messages, follow-ups), Event Intent (planning fields), Related (account, contact, opportunity), AI Insights (recommendations)." },
  { section: "Accounts", path: "/crm/accounts", roles: ["CRM_MANAGER", "RELATIONSHIP_MANAGER", "BUSINESS_OWNER"],
    desc: "Company/organisation records. Created automatically on lead conversion or manually." },
  { section: "Contacts", path: "/crm/contacts", roles: ["CRM_MANAGER", "RELATIONSHIP_MANAGER", "BUSINESS_OWNER"],
    desc: "Individual customer records. Linked to accounts and leads." },
  { section: "Opportunities", path: "/crm/opportunities", roles: ["CRM_MANAGER", "RELATIONSHIP_MANAGER"],
    desc: "Sales pipeline records. Progress through: RFP → Technical Review → Proposal → Negotiation → Contract → Closed Won." },
  { section: "Activities", path: "/crm/activities", roles: ["CRM_MANAGER", "RELATIONSHIP_MANAGER", "EVENT_PLANNER_DESIGNER"],
    desc: "Cross-lead view of all tasks, calls, meetings, and notes assigned to you or across the team." },
  { section: "Follow-ups", path: "/engage/follow-ups", roles: ["RELATIONSHIP_MANAGER", "EVENT_PLANNER_DESIGNER"],
    desc: "Your scheduled follow-ups sorted by due date. Overdue follow-ups are highlighted." },
  { section: "Comm Inbox", path: "/crm/inbox", roles: ["CRM_MANAGER", "RELATIONSHIP_MANAGER", "EVENT_PLANNER_DESIGNER"],
    desc: "Inbound messages (WhatsApp / Email) from customers. Link to a lead to route the conversation." },
  { section: "Customer Journeys", path: "/journeys", roles: ["RELATIONSHIP_MANAGER"],
    desc: "Complete customer interaction timeline: all touchpoints from first inquiry through event execution." },
  { section: "My Planning Cases", path: "/planner/cases", roles: ["EVENT_PLANNER_DESIGNER"],
    desc: "Planner home: assigned cases with planning readiness %, missing info, and next-action prompt." },
  { section: "Event Planning", path: "/imagine", roles: ["PROJECT_MANAGER", "TENANT_ADMIN"],
    desc: "Event brief and blueprint view once Start Event Planning has been triggered." },
  { section: "Active Events", path: "/events", roles: ["PROJECT_MANAGER", "TENANT_ADMIN"],
    desc: "Live events: threads, change engine, readiness milestones, 360 view." },
  { section: "Setup Studio", path: "/setup-studio", roles: ["TENANT_ADMIN"],
    desc: "CRM configuration: activate service catalogue, event types, agent policies." },
  { section: "Users & Roles", path: "/administration/users", roles: ["TENANT_ADMIN"],
    desc: "Create and manage user accounts. Assign roles." },
  { section: "Audit", path: "/administration/audit", roles: ["TENANT_ADMIN"],
    desc: "Immutable log of all platform actions for compliance and traceability." },
  { section: "Platform Administration", path: "/admin", roles: ["TENANT_ADMIN"],
    desc: "System configuration, user management, and tenant settings." },
];

// ── Glossary ───────────────────────────────────────────────────────────────────

const GLOSSARY = [
  { term: "Lead", def: "A potential customer enquiry that has been received and is being tracked. A lead moves through stages from NEW to CONVERTED." },
  { term: "Lead Status", def: "The stage the lead is at in the sales pipeline: NEW → CONTACTED → QUALIFYING → QUALIFIED → CONVERTED. Side branches: NURTURE, DISQUALIFIED, CLOSED. Large events may pass through a Planning Review stage before qualifying." },
  { term: "Qualification Status", def: "An automatically computed assessment of how complete the lead information is: UNQUALIFIED (score < 20) → PARTIALLY_QUALIFIED (20–74) → QUALIFIED (≥ 75) → PLANNING_REVIEW_REQUIRED (guest ≥ 500 and qualified)." },
  { term: "Planning Review Required", def: "Qualification status flag for large events (≥ 500 guests) that meet the qualification threshold. Signals that a senior review is recommended before proceeding. Resolve by transitioning the lead and then moving to QUALIFIED or DISQUALIFIED." },
  { term: "Relationship Manager", def: "The Prashasta team member who commercially owns a lead. A Relationship Manager claims leads from the pool, qualifies prospects, and drives the customer relationship through to conversion. Only a Relationship Manager can be the canonical commercial owner of a lead." },
  { term: "Project Manager", def: "The execution owner for a live event. Manages planning milestones, vendor coordination, and delivery after a lead has been converted and event planning is underway." },
  { term: "Journey", def: "The complete, chronological history of a customer's relationship with Prashasta — from first inquiry through lead qualification, conversion, and event execution." },
  { term: "Account", def: "A company or organisation record. Created automatically when a lead is converted. Multiple leads from the same company share one Account." },
  { term: "Contact", def: "An individual person (customer) record. Linked to an Account. Created on lead conversion." },
  { term: "Opportunity", def: "A formal sales record tracking deal value and stage. Created when a lead is converted. Progresses through the sales cycle to Closed Won." },
  { term: "Activity", def: "Any interaction logged against a lead: calls, meetings, notes, tasks, messages. The full activity history forms the lead's communication timeline." },
  { term: "Follow-up", def: "A scheduled future action with a due date and time. Appears in Upcoming / Overdue sections. Can be completed or rescheduled." },
  { term: "Event Intent", def: "The planning-specific fields of a lead: event type, date, guest count, location, budget range, venue status, services of interest, customer requirements." },
  { term: "Brief", def: "The event planning brief document — captures vision, themes, service scope, and customer preferences. Created when Event Planning starts." },
  { term: "Blueprint", def: "The detailed event execution plan — tasks, milestones, vendor assignments, and timelines. Evolves from the Brief." },
  { term: "Planning Scope", def: "Whether Prashasta manages the entire event (END_TO_END) or specific modules only (PARTIAL)." },
  { term: "Start Event Planning", def: "The action that transitions a Closed Won opportunity into an active Event. Creates exactly one Event Draft and Brief (idempotent). Requires CLOSED_WON opportunity stage." },
  { term: "Duplicate Resolution", def: "When converting a lead, if a matching Account or Contact already exists, the system prompts you to either use the existing record or create a new one." },
  { term: "AI Recommendation", def: "A suggestion generated by the platform's policy engine for a Relationship Manager or Planner — e.g. 'Recommend Décor package for this occasion type'. Can be accepted, rejected, or modified." },
  { term: "Communication Inbox (Comm Inbox)", def: "The inbound message queue for WhatsApp and Email messages from customers. Messages that cannot be automatically matched to a lead appear here for manual linking." },
];

// ── Components ────────────────────────────────────────────────────────────────

function Accordion({ title, icon: Icon, color = "gold", children }) {
  const [open, setOpen] = useState(false);
  const colorMap = {
    gold:  { bg: "#FDF6EC", border: "#E7D8C0", icon: "var(--brand-gold)", text: "#78400A" },
    blue:  { bg: "#EEF4FF", border: "#C5D9F5", icon: "#0176D3", text: "#014486" },
    green: { bg: "#EDF7EE", border: "#B3DFB8", icon: "#2E844A", text: "#1A4F2A" },
    rose:  { bg: "#FFF0F0", border: "#FCC5C5", icon: "#BA0517", text: "#8B0000" },
  };
  const c = colorMap[color] || colorMap.gold;
  return (
    <div className="rounded-xl border mb-3 overflow-hidden transition-all" style={{ borderColor: c.border }}>
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
        style={{ background: c.bg }}
        onClick={() => setOpen(v => !v)}
      >
        {Icon && <Icon className="w-4 h-4 flex-shrink-0" style={{ color: c.icon }} />}
        <span className="flex-1 text-sm font-semibold" style={{ color: c.text }}>{title}</span>
        {open ? <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: c.icon }} />
               : <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: c.icon }} />}
      </button>
      {open && (
        <div className="px-4 py-3 bg-white border-t" style={{ borderColor: c.border }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Steps({ steps }) {
  return (
    <ol className="space-y-3 mt-1">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5"
            style={{ background: "var(--brand-gold)", color: "#fff" }}>
            {i + 1}
          </span>
          <div>
            <p className="text-xs font-semibold" style={{ color: "#57534E" }}>
              <span className="font-mono text-[11px] px-1.5 py-0.5 rounded mr-1"
                style={{ background: "#F0E8DA", color: "var(--brand-gold)" }}>
                {s.nav}
              </span>
            </p>
            <p className="text-sm mt-0.5" style={{ color: "#1C1917" }}>{s.action}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function CapList({ items, type }) {
  return (
    <ul className="space-y-1.5 mt-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          {type === "can"
            ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            : <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />}
          <span style={{ color: "#1C1917" }}>{item}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Tab definitions ────────────────────────────────────────────────────────────

const TABS = [
  { id: "role",    label: "My Role",         icon: User },
  { id: "workflows", label: "Key Workflows", icon: Zap },
  { id: "nav",     label: "Navigation Map",  icon: MapPin },
  { id: "glossary", label: "Glossary",       icon: BookOpen },
];

// ── Main Component ─────────────────────────────────────────────────────────────

export default function UserGuide() {
  const { user } = useAuth();
  const role = user?.role || "BUSINESS_OWNER";
  const [tab, setTab] = useState("role");
  const [glossarySearch, setGlossarySearch] = useState("");
  const [navSearch, setNavSearch] = useState("");

  const roleLabel = ROLE_LABELS[role] || role;
  const description = ROLE_DESCRIPTIONS[role] || "";
  const can = ROLE_CAN[role] || [];
  const cannot = ROLE_CANNOT[role] || [];
  const workflows = WORKFLOWS[role] || [];

  const filteredGlossary = glossarySearch.trim()
    ? GLOSSARY.filter(g =>
        g.term.toLowerCase().replace(/_/g, " ").includes(glossarySearch.toLowerCase()) ||
        g.term.toLowerCase().includes(glossarySearch.toLowerCase()) ||
        g.def.toLowerCase().includes(glossarySearch.toLowerCase()))
    : GLOSSARY;

  const filteredNav = navSearch.trim()
    ? NAV_MAP.filter(n =>
        n.section.toLowerCase().includes(navSearch.toLowerCase()) ||
        n.desc.toLowerCase().includes(navSearch.toLowerCase()))
    : NAV_MAP;

  return (
    <div className="h-full overflow-y-auto" style={{ background: "#F4EFEA" }}>
      <div className="max-w-4xl mx-auto px-4 py-6">

        {/* Header */}
        <div className="mb-6 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--brand-gold)" }}>
            <HelpCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-display" style={{ color: "#1C1917" }}>
              User Guide
            </h1>
            <p className="text-sm mt-0.5" style={{ color: "#78716C" }}>
              Logged in as <span className="font-semibold" style={{ color: "var(--brand-gold)" }}>{roleLabel}</span>
              {" · "} Role-specific guidance for how to use ONE Engage CRM
            </p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ background: "#EDE5DB" }}>
          {TABS.map(({ id, label, icon: Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                data-testid={`guide-tab-${id}`}
                onClick={() => setTab(id)}
                className="flex items-center gap-1.5 flex-1 justify-center px-3 py-2 rounded-lg text-sm font-medium transition-all"
                style={active
                  ? { background: "#FFFFFF", color: "var(--brand-gold)", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
                  : { color: "#78716C" }}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            );
          })}
        </div>

        {/* ── Tab: My Role ─────────────────────────────────────────────── */}
        {tab === "role" && (
          <div className="space-y-5">
            {/* Role summary card */}
            <div className="rounded-xl p-5 border" style={{ background: "#FFFDF9", borderColor: "#E7D8C0" }}>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4" style={{ color: "var(--brand-gold)" }} />
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--brand-gold)" }}>
                  {roleLabel}
                </span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: "#1C1917" }}>{description}</p>
            </div>

            {/* Can / Cannot */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl p-4 border" style={{ background: "#F3FBF4", borderColor: "#B3DFB8" }}>
                <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: "#2E844A" }}>
                  <CheckCircle2 className="w-4 h-4" /> What you CAN do
                </h3>
                <CapList items={can} type="can" />
              </div>
              <div className="rounded-xl p-4 border" style={{ background: "#FFF8F8", borderColor: "#FCC5C5" }}>
                <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: "#BA0517" }}>
                  <XCircle className="w-4 h-4" /> What you CANNOT do
                </h3>
                <CapList items={cannot} type="cannot" />
              </div>
            </div>

            {/* Quick tip */}
            <div className="rounded-xl p-4 flex gap-3 border" style={{ background: "#FFFAEC", borderColor: "#E7D8C0" }}>
              <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--brand-gold)" }} />
              <div>
                <p className="text-xs font-bold mb-1" style={{ color: "#78400A" }}>Pro tip</p>
                <p className="text-sm" style={{ color: "#1C1917" }}>
                  {role === "EVENT_PLANNER_DESIGNER"
                    ? "Your Planning Cases dashboard shows a readiness % per lead. Focus on the 'Missing Info' leads first — filling in event date, location, and budget unlocks the most qualification points."
                    : role === "RELATIONSHIP_MANAGER"
                    ? "Claim leads before they go stale — the pool updates in real time. Once claimed, your Lead 360 becomes the single source of truth for the customer relationship."
                    : role === "PROJECT_MANAGER"
                    ? "The readiness gates (Design, Commercial, Partner, Operations, Event Day) show what still needs attention. Work backwards from the event date — partner sign-offs typically take the longest."
                    : role === "CRM_MANAGER"
                    ? "The CRM dashboard shows leads waiting longest without activity. Use the 'No activity in 3+ days' filter to catch deals that are going cold."
                    : role === "BUSINESS_OWNER"
                    ? "The pipeline funnel on Home updates in real time. A large gap between Qualifying and Qualified usually means large-event leads need a Planning Review before they can proceed."
                    : role === "TENANT_ADMIN"
                    ? "Setup Studio shows a readiness score per domain. Activate domains in order — each one unlocks the next set of platform features."
                    : "Use the global search (⌘K or the search icon in the top bar) to quickly find any lead, account, or contact by name, phone, or email."
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Key Workflows ───────────────────────────────────────── */}
        {tab === "workflows" && (
          <div>
            {workflows.length === 0 ? (
              <div className="text-center py-12 text-sm" style={{ color: "#78716C" }}>
                No workflows defined for this role yet.
              </div>
            ) : (
              <>
                <p className="text-sm mb-4" style={{ color: "#78716C" }}>
                  Step-by-step guides for the most common tasks for your role. Click any workflow to expand it.
                </p>
                {workflows.map((wf, i) => (
                  <Accordion key={i} title={wf.title} icon={ArrowRight} color="gold">
                    <Steps steps={wf.steps} />
                  </Accordion>
                ))}
              </>
            )}

            {/* Planning Review workflow — universal */}
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: "#78716C" }}>
                Universal Workflows
              </p>
              <Accordion title="What to do when a lead is flagged for Planning Review" icon={AlertTriangle} color="rose">
                <p className="text-sm mb-3" style={{ color: "#57534E" }}>
                  This qualification status appears automatically when a lead has ≥ 500 guests <em>and</em> meets the qualification threshold. It signals that a senior review is recommended before moving forward.
                </p>
                <Steps steps={[
                  { nav: "Leads", action: "Filter by Qualification = 'Planning Review Required'. These leads also appear highlighted in the CRM Manager / Business Owner dashboard." },
                  { nav: "Lead Detail → Overview tab", action: "Review event details, guest count (≥ 500 triggers this flag), and qualification score." },
                  { nav: "Lead Detail → Status path bar", action: "Transition the lead status to the Planning Review stage — this signals that review is in progress." },
                  { nav: "Lead Detail → Overview", action: "Review the event details: date, guest count, budget, services. Check what information is still missing." },
                  { nav: "Lead Detail → Status path bar", action: "Transition the lead status to the Planning Review stage — this signals that senior review is in progress." },
                  { nav: "After review", action: "Transition to 'Qualified' if the event is viable, or 'Disqualified' if it should not proceed. The qualification status will recalculate each time the lead is opened." },
                ]} />
              </Accordion>
              <Accordion title="How to search for any customer record" icon={Search} color="blue">
                <Steps steps={[
                  { nav: "Global Search (top bar, or ⌘K / Ctrl+K)", action: "Type any part of the customer name, phone number, email, account name, or lead number (e.g. LEAD-0042)." },
                  { nav: "Results panel", action: "Results are grouped by type: Leads, Accounts, Contacts, Opportunities. Click any result to open it directly." },
                  { nav: "Advanced filters", action: "On the Leads page, use the filter bar (Status, Qualification, Date range) for a more targeted search." },
                ]} />
              </Accordion>
            </div>
          </div>
        )}

        {/* ── Tab: Navigation Map ──────────────────────────────────────── */}
        {tab === "nav" && (
          <div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#78716C" }} />
              <input
                data-testid="guide-nav-search"
                className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border outline-none transition-colors"
                style={{ background: "#FFFFFF", borderColor: "#E7D8C0", color: "#1C1917" }}
                placeholder="Search pages and sections..."
                value={navSearch}
                onChange={e => setNavSearch(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              {filteredNav.map((item, i) => (
                <div key={i} className="rounded-xl p-4 border" style={{ background: "#FFFDF9", borderColor: "#E7D8C0" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold" style={{ color: "#1C1917" }}>{item.section}</p>
                        <code className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                          style={{ background: "#F0E8DA", color: "#78400A" }}>
                          {item.path}
                        </code>
                      </div>
                      <p className="text-sm" style={{ color: "#57534E" }}>{item.desc}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(item.roles.includes("ALL") ? Object.keys(ROLE_LABELS) : item.roles).map(r => (
                      <span key={r}
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                        style={{
                          background: r === role ? "var(--brand-gold)" : "#F0E8DA",
                          color: r === role ? "#FFFFFF" : "#78400A",
                          fontWeight: r === role ? 700 : 400,
                        }}>
                        {ROLE_LABELS[r] || r}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {filteredNav.length === 0 && (
                <p className="text-center py-8 text-sm" style={{ color: "#78716C" }}>No results for "{navSearch}"</p>
              )}
            </div>
          </div>
        )}

        {/* ── Tab: Glossary ────────────────────────────────────────────── */}
        {tab === "glossary" && (
          <div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#78716C" }} />
              <input
                data-testid="guide-glossary-search"
                className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border outline-none transition-colors"
                style={{ background: "#FFFFFF", borderColor: "#E7D8C0", color: "#1C1917" }}
                placeholder="Search terms..."
                value={glossarySearch}
                onChange={e => setGlossarySearch(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              {filteredGlossary.map((item, i) => (
                <div key={i} className="rounded-xl p-4 border" style={{ background: "#FFFDF9", borderColor: "#E7D8C0" }}>
                  <div className="flex items-start gap-3">
                    <Tag className="w-3.5 h-3.5 mt-1 flex-shrink-0" style={{ color: "var(--brand-gold)" }} />
                    <div>
                      <p className="text-sm font-bold" style={{ color: "#1C1917" }}>{item.term}</p>
                      <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>{item.def}</p>
                    </div>
                  </div>
                </div>
              ))}
              {filteredGlossary.length === 0 && (
                <p className="text-center py-8 text-sm" style={{ color: "#78716C" }}>No results for "{glossarySearch}"</p>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
